import moment from 'moment';
import { useCallback, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { queryGet, queryReplace } from './fetch';
import { TdmzEvent } from './taskSetting';
import { useCurrentOrganizationId } from './currentOrganization';

export interface IEvent {
  start: Date;
  end: Date;
}

export interface Availability extends IEvent {
  id: string;
  start: Date;
  end: Date;
  taskSettingId: string;
}

export interface ReplaceAvailabilityDto {
  id: string;
  start: Date;
  end: Date;
}

export const useTaskSettingsAvailabilities = (id?: string) => {
  return useQuery<Availability[]>(
    ['task-settings', id, 'availabilities'],
    queryGet(`/v2/task-settings/${id}/availabilities`),
    {
      enabled: !!id,
    },
  );
};

export const useReplaceTaskSettingsAvailabilities = (id?: string) => {
  const queryClient = useQueryClient();
  const onSuccess = useCallback(
    (res: Availability[]) => {
      queryClient.setQueryData(['task-settings', id, 'availabilities'], res);
    },
    [queryClient, id],
  );
  return useMutation<
    Availability[],
    undefined,
    { availabilities: ReplaceAvailabilityDto[] }
  >(queryReplace(`/v2/task-settings/${id}/availabilities`), {
    onSuccess,
  });
};

export const useClearAvailableDatesCache = (taskSettingId?: string) => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries([
        'task-settings',
        taskSettingId,
        'available-dates',
      ]),
    [queryClient, taskSettingId],
  );
};

export const useAvailableDates = (
  taskSettingId?: string,
  enabled?: boolean,
) => {
  return useInfiniteQuery<{ dates: Date[]; nextPageIsEmpty: boolean }>(
    ['task-settings', taskSettingId, 'available-dates'],
    async ({ pageParam }) => {
      const res = await queryGet(
        `/v2/task-settings/${taskSettingId}/available-dates`,
        { pageParam },
      )();
      const dates = res.dates;
      return {
        dates: dates.map((d: string) => moment(d).toDate()),
        nextPageIsEmpty: res.nextPageIsEmpty,
      };
    },
    {
      enabled: !!taskSettingId && enabled,
      getNextPageParam: (lastPage) => {
        return lastPage.nextPageIsEmpty
          ? undefined
          : lastPage.dates[lastPage.dates.length - 1];
      },
    },
  );
};

export const useAvailableDatesForRescheduleTask = (taskId?: string) => {
  return useInfiniteQuery<{ dates: Date[]; nextPageIsEmpty: boolean }>(
    ['tasks', taskId, 'available-dates-for-reschedule'],
    async ({ pageParam }) => {
      const res = await queryGet(
        `/v2/tasks/${taskId}/available-dates-for-reschedule`,
        { pageParam },
      )();
      const dates = res.dates;
      return {
        dates: dates.map((d: string) => moment(d).toDate()),
        nextPageIsEmpty: res.nextPageIsEmpty,
      };
    },
    {
      enabled: !!taskId,
      getNextPageParam: (lastPage) => {
        if (lastPage.nextPageIsEmpty) {
          return undefined;
        }
        if (lastPage.dates.length === 0) {
          return undefined;
        }
        return lastPage.dates[lastPage.dates.length - 1];
      },
    },
  );
};

export const useGetModeratorsMeetingEvents = (
  moderatorId?: string,
  organizationId?: string,
) => {
  const cache = useRef<Record<string, TdmzEvent[]>>({});

  return useCallback(
    async (params: any) => {
      if (!moderatorId || !organizationId) {
        return [];
      }

      const cacheKey = JSON.stringify([
        'organizations',
        organizationId,
        'accounts',
        moderatorId,
        'meetings-events',
        params,
      ]);
      if (cache.current[cacheKey]) {
        return cache.current[cacheKey];
      }
      const res = await queryGet<TdmzEvent[]>(
        `/v2/organizations/${organizationId}/accounts/${moderatorId}/meetings-events`,
        params,
      )();
      cache.current[cacheKey] = res;
      return res;
    },
    [cache, moderatorId],
  );
};
